/**
 * SELVANE Homepage — Shared JavaScript v21.7
 * "Correspondence Fix + Video/Image Hero" Release
 *
 * v21.7 CHANGES (over v21.6):
 *   1. FIX: Correspondence hero image visible on mobile (CSS fix)
 *   2. NEW: Video/Image toggle support for all hero sections
 *   3. Auto-play video management for hero media elements
 *
 * v21.6 CHANGES (over v21.5):
 *   1. BREATHING ROOM: Each drawer layer gets a "dwell spacer" at the bottom
 *      - Adds a transparent div (30vh height) after each layer's content
 *      - This forces the user to scroll further before the next layer covers
 *      - Prevents the "too fast" transition between drawers
 *      - Loewe uses the same technique (extra scroll distance between layers)
 *
 *   2. LOGO: Full-width on mobile (~90vw), larger on desktop (~42vw)
 *      - CSS change only (see selvane-homepage.css v21.6)
 *
 * v21.5 CHANGES (over v21.4):
 *   1. STACKING: Grouped drawer layers (Editorial Hero + Product Grid = ONE layer)
 *      - Layer grouping: sections with matching collection are merged
 *      - Negative top values for layers taller than viewport (Loewe technique)
 *      - Removed overlay darkening (Loewe doesn't use it)
 *      - No z-index — DOM order handles stacking
 *
 *   2. PRODUCT GRID: aspect-ratio: 3/4 (Brand Bible) + gap: 0px (Loewe)
 *      - Fixes collapsed 69px shelf caused by lazy-loaded images with no fallback
 *
 *   3. LOGO: Malachite fadeout synced to 0.5s (matches sticky bar slide-in)
 *
 * ARCHITECTURE (after JS):
 *   <main id="MainContent">
 *     <div id="selvane-drawer-stack" class="selvane-drawer-stack">
 *       <!-- Layer 0: Campaign Module (Hero + Text Block + Triptych) -->
 *       <div class="selvane-drawer-layer" style="position:sticky; top:-Npx">
 *         <div class="shopify-section selvane-hero-wrapper">...</div>
 *         <div class="shopify-section selvane-text-block-wrapper">...</div>
 *         <div class="shopify-section selvane-triptych-wrapper">...</div>
 *       </div>
 *       <!-- Layer 1: RTW Collection (Editorial Hero + Product Shelf merged) -->
 *       <div class="selvane-drawer-layer" style="position:sticky; top:-Npx">
 *         <div class="shopify-section selvane-editorial-hero-wrapper">...</div>
 *         <div class="shopify-section selvane-product-shelf-wrapper">...</div>
 *       </div>
 *       <!-- Layer 2: Correspondence (standalone) -->
 *       <div class="selvane-drawer-layer" style="position:sticky; top:0">
 *         <div class="shopify-section selvane-correspondence-wrapper">...</div>
 *       </div>
 *       <!-- Layer 3: LG Collection (Editorial Hero + Four-up merged) -->
 *       <div class="selvane-drawer-layer" style="position:sticky; top:-Npx">
 *         <div class="shopify-section selvane-editorial-hero-wrapper">...</div>
 *         <div class="shopify-section selvane-four-up-grid-wrapper">...</div>
 *       </div>
 *       <!-- Layer 4: Dual Entry (standalone) -->
 *       <div class="selvane-drawer-layer" style="position:sticky; top:0">
 *         <div class="shopify-section selvane-dual-entry-wrapper">...</div>
 *       </div>
 *     </div>
 *   </main>
 */

(function () {
  'use strict';

  /* Guard: prevent double initialization */
  if (window.__selvaneHomepageV21_7) return;
  window.__selvaneHomepageV21_7 = true;
  window.__selvaneHomepageV21_6 = true;
  window.__selvaneHomepageV21_5 = true;
  window.__selvaneHomepageV21_4 = true;
  window.__selvaneHomepageV21_3 = true;
  window.__selvaneHomepageV21_2 = true;
  window.__selvaneHomepageV21_1 = true;
  window.__selvaneHomepageV21 = true;
  window.__selvaneHomepageV20 = true;

  /* ─── Utilities ─── */
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); };

  /* ─── State ─── */
  var stickyBarActive = false;
  var activeZoneTitle = '';
  var rafId = 0;
  var headerHeight = 80;
  var cachedZones = [];
  var zoneMapDirty = true;
  var drawerLayers = []; /* .selvane-drawer-layer elements */

  /* ─── DOM References ─── */
  var malachiteLogo, stickyBarEl, sectionHeader, agileHeaderEl, drawerStack;


  /* ═══════════════════════════════════════════════════════════════
   *  MEASURE AGILE HEADER
   * ═══════════════════════════════════════════════════════════════ */
  function measureHeader() {
    sectionHeader = $('.section-header');
    agileHeaderEl = $('header.header') || $('header');

    if (agileHeaderEl) {
      headerHeight = agileHeaderEl.offsetHeight || 80;
    }
    return headerHeight;
  }


  /* ═══════════════════════════════════════════════════════════════
   *  MALACHITE LOGO — Move into .section-header
   * ═══════════════════════════════════════════════════════════════ */
  function positionMalachiteLogo() {
    if (!malachiteLogo) return;
    if (!sectionHeader) sectionHeader = $('.section-header');
    if (!sectionHeader) return;

    if (malachiteLogo.parentElement !== sectionHeader) {
      sectionHeader.appendChild(malachiteLogo);
    }

    malachiteLogo.style.top = headerHeight + 'px';
  }


  /* ═══════════════════════════════════════════════════════════════
   *  RENDER STICKY BAR (once, on init)
   * ═══════════════════════════════════════════════════════════════ */
  function renderStickyBar() {
    if ($('.selvane-sticky-bar')) return $('.selvane-sticky-bar');

    var bar = document.createElement('div');
    bar.className = 'selvane-sticky-bar';
    bar.id = 'selvane-sticky-bar';
    bar.innerHTML =
      '<span class="selvane-sticky-bar__title"></span>' +
      '<nav class="selvane-sticky-bar__nav"></nav>';
    document.body.appendChild(bar);
    return bar;
  }


  /* ═══════════════════════════════════════════════════════════════
   *  EXTRACT SECTION TITLE FROM DOM
   * ═══════════════════════════════════════════════════════════════ */
  function extractSectionTitle(sectionEl) {
    if (!sectionEl) return '';

    var zoneEl = sectionEl.querySelector('[data-selvane-zone]');
    if (zoneEl) {
      var zoneVal = zoneEl.getAttribute('data-selvane-zone');
      if (zoneVal && zoneVal.trim() !== '') return zoneVal.trim();
    }

    var titleEls = sectionEl.querySelectorAll('[class*="__title"]');
    for (var t = 0; t < titleEls.length; t++) {
      var txt = titleEls[t].textContent.trim();
      if (txt && txt.length > 0 && txt.length < 80) return txt;
    }

    var headings = sectionEl.querySelectorAll('h1, h2, h3');
    for (var h = 0; h < headings.length; h++) {
      var htxt = headings[h].textContent.trim();
      if (htxt && htxt.length > 0 && htxt.length < 80) return htxt;
    }

    var labels = sectionEl.querySelectorAll('.selvane-dual-entry__label');
    if (labels.length > 0) {
      var labelTexts = [];
      for (var l = 0; l < labels.length; l++) {
        var lt = labels[l].textContent.trim();
        if (lt) labelTexts.push(lt);
      }
      if (labelTexts.length > 0) return labelTexts.join(' · ');
    }

    var entryImgs = sectionEl.querySelectorAll('.selvane-dual-entry__img, .selvane-triptych__img');
    if (entryImgs.length > 0) {
      var altTexts = [];
      for (var a = 0; a < entryImgs.length; a++) {
        var alt = entryImgs[a].alt;
        if (alt && alt.trim()) altTexts.push(alt.trim());
      }
      if (altTexts.length > 0) return altTexts.join(' · ');
    }

    return '';
  }


  /* ═══════════════════════════════════════════════════════════════
   *  BUILD ZONE MAP
   * ═══════════════════════════════════════════════════════════════ */
  function buildZoneMap() {
    var zones = [];
    var sections = $$('.shopify-section');

    for (var j = 0; j < sections.length; j++) {
      var sec = sections[j];
      var id = (sec.id || '').toLowerCase();

      if (id.indexOf('header') !== -1 ||
          id.indexOf('footer') !== -1 ||
          id.indexOf('announcement') !== -1 ||
          id.indexOf('knot-loader') !== -1 ||
          id.indexOf('knot_loader') !== -1) continue;

      var secRect = sec.getBoundingClientRect();
      if (secRect.height < 60) continue;

      var secTitle = extractSectionTitle(sec);
      zones.push({
        el: sec,
        top: secRect.top,
        bottom: secRect.bottom,
        title: secTitle
      });
    }

    zones.sort(function (a, b) { return a.top - b.top; });

    var lastTitle = '';
    for (var k = 0; k < zones.length; k++) {
      if (zones[k].title) {
        lastTitle = zones[k].title;
      } else {
        zones[k].title = lastTitle;
      }
    }

    return zones;
  }


  /* ═══════════════════════════════════════════════════════════════
   *  LAYER GROUPING LOGIC
   *
   *  Determines which consecutive sections should be merged
   *  into a single sticky layer. Rules:
   *    - Campaign Module: hero + text-block + triptych = ONE layer (L0)
   *    - editorial-hero + product-shelf = ONE layer (RTW collection)
   *    - editorial-hero + four-up-grid = ONE layer (LG collection)
   *    - Everything else = standalone layer
   *
   *  Returns array of arrays: [[sec0, sec1, sec2], [sec3, sec4], ...]
   * ═══════════════════════════════════════════════════════════════ */
  function computeLayerGroups(sections) {
    var groups = [];
    var i = 0;

    while (i < sections.length) {
      var sec = sections[i];
      var id = (sec.id || '').toLowerCase();
      var cls = (sec.className || '').toLowerCase();

      /* Campaign Module: hero + text-block + triptych + (optional product-shelf) = ONE layer */
      if ((id.indexOf('selvane_hero') !== -1 || cls.indexOf('selvane-hero') !== -1) &&
          i + 2 < sections.length) {
        var next1Id = (sections[i + 1].id || '').toLowerCase();
        var next1Cls = (sections[i + 1].className || '').toLowerCase();
        var next2Id = (sections[i + 2].id || '').toLowerCase();
        var next2Cls = (sections[i + 2].className || '').toLowerCase();

        var isTextBlock = next1Id.indexOf('text_block') !== -1 || next1Cls.indexOf('text-block') !== -1;
        var isTriptych = next2Id.indexOf('triptych') !== -1 || next2Cls.indexOf('triptych') !== -1;

        if (isTextBlock && isTriptych) {
          /* Check if a product-shelf follows the triptych — include it in the Campaign Module */
          if (i + 3 < sections.length) {
            var next3Cls = (sections[i + 3].className || '').toLowerCase();
            if (next3Cls.indexOf('product-shelf') !== -1) {
              /* Merge: Campaign Hero + Text Block + Triptych + Product Shelf = Campaign Module */
              groups.push([sections[i], sections[i + 1], sections[i + 2], sections[i + 3]]);
              i += 4;
              continue;
            }
          }
          /* Merge: Campaign Hero + Text Block + Triptych = Campaign Module */
          groups.push([sections[i], sections[i + 1], sections[i + 2]]);
          i += 3;
          continue;
        }
      }

      /* Also check hero + triptych (without text block) */
      if ((id.indexOf('selvane_hero') !== -1 || cls.indexOf('selvane-hero') !== -1) &&
          i + 1 < sections.length) {
        var nextId = (sections[i + 1].id || '').toLowerCase();
        var nextCls = (sections[i + 1].className || '').toLowerCase();
        if (nextId.indexOf('triptych') !== -1 || nextCls.indexOf('triptych') !== -1) {
          groups.push([sections[i], sections[i + 1]]);
          i += 2;
          continue;
        }
      }

      /* Collection: editorial-hero + product grid = ONE layer */
      if (cls.indexOf('editorial-hero') !== -1 && i + 1 < sections.length) {
        var nextCls2 = (sections[i + 1].className || '').toLowerCase();
        if (nextCls2.indexOf('product-shelf') !== -1 ||
            nextCls2.indexOf('four-up-grid') !== -1) {
          groups.push([sections[i], sections[i + 1]]);
          i += 2;
          continue;
        }
      }

      /* Standalone layer */
      groups.push([sections[i]]);
      i++;
    }

    return groups;
  }


  /* ═══════════════════════════════════════════════════════════════
   *  BUILD DRAWER STACK — Create wrapper with grouped layers
   *
   *  1. Find all consecutive .shopify-section with .selvane-drawer-reveal
   *  2. Group them into layers (editorial hero + product grid = one layer)
   *  3. Wrap each group in a .selvane-drawer-layer div
   *  4. Wrap all layers in #selvane-drawer-stack
   *  5. Apply position:sticky + dynamic top values
   * ═══════════════════════════════════════════════════════════════ */
  function buildDrawerStack() {
    /* Already built? Just recalculate top values */
    if (document.getElementById('selvane-drawer-stack')) {
      drawerStack = document.getElementById('selvane-drawer-stack');
      drawerLayers = Array.from(drawerStack.querySelectorAll(':scope > .selvane-drawer-layer'));
      applyStackStyles();
      return;
    }

    var main = document.getElementById('MainContent');
    if (!main) return;

    var allChildren = Array.from(main.children);

    /* Find all shopify-section elements that should be part of the drawer stack.
     * This includes:
     *   - Sections with .selvane-drawer-reveal (triptych, editorial hero, etc.)
     *   - Campaign Hero section (selvane_hero)
     *   - Text Block section between hero and triptych (selvane_text_block)
     */
    var drawerSectionEls = [];
    for (var i = 0; i < allChildren.length; i++) {
      var child = allChildren[i];
      if (!child.classList || !child.classList.contains('shopify-section')) continue;

      var childId = (child.id || '').toLowerCase();

      /* Include drawer-reveal sections */
      if (child.querySelector('.selvane-drawer-reveal')) {
        drawerSectionEls.push(child);
        continue;
      }

      /* Include Campaign Hero section */
      if (childId.indexOf('selvane_hero') !== -1) {
        drawerSectionEls.push(child);
        continue;
      }

      /* Include Text Block section (between hero and triptych) */
      if (childId.indexOf('selvane_text_block') !== -1) {
        drawerSectionEls.push(child);
        continue;
      }
    }

    if (drawerSectionEls.length < 2) return;

    /* Compute layer groups */
    var groups = computeLayerGroups(drawerSectionEls);

    /* Create the stack wrapper */
    var wrapper = document.createElement('div');
    wrapper.id = 'selvane-drawer-stack';
    wrapper.className = 'selvane-drawer-stack';

    /* Insert wrapper before the first drawer section */
    drawerSectionEls[0].parentNode.insertBefore(wrapper, drawerSectionEls[0]);

    /* Create layer wrappers and move sections into them */
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var layer = document.createElement('div');
      layer.className = 'selvane-drawer-layer';

      for (var s = 0; s < group.length; s++) {
        layer.appendChild(group[s]);
      }

      /* v21.6: Add breathing room spacer at the bottom of each layer
       * (except the last layer — nothing follows it)
       * This creates a "dwell zone" so users can browse the current
       * layer's content before the next layer starts covering it.
       * Height: 35vh — enough to scroll comfortably through product grids.
       */
      if (g < groups.length - 1) {
        var spacer = document.createElement('div');
        spacer.className = 'selvane-dwell-spacer';
        spacer.style.height = '35vh';
        spacer.style.minHeight = '200px';
        spacer.style.maxHeight = '400px';
        spacer.style.pointerEvents = 'none';
        spacer.setAttribute('aria-hidden', 'true');
        layer.appendChild(spacer);
      }

      wrapper.appendChild(layer);
    }

    drawerStack = wrapper;
    drawerLayers = Array.from(wrapper.querySelectorAll(':scope > .selvane-drawer-layer'));

    applyStackStyles();
  }


  /* ═══════════════════════════════════════════════════════════════
   *  APPLY STACK STYLES — Set sticky + dynamic top on each layer
   *
   *  Loewe technique:
   *    - If layer height > viewport height:
   *        top = -(layerHeight - viewportHeight)
   *      This ensures the layer fills exactly the viewport when fully stuck
   *    - If layer height <= viewport height:
   *        top = 0
   *    - No z-index needed — DOM order handles stacking
   * ═══════════════════════════════════════════════════════════════ */
  function applyStackStyles() {
    var vh = window.innerHeight;

    for (var i = 0; i < drawerLayers.length; i++) {
      var layer = drawerLayers[i];
      var layerHeight = layer.offsetHeight;

      /* v21.6: Subtract dwell spacer height from the calculation.
       * The spacer is transparent scroll distance — it should NOT
       * affect the sticky top value. We want the CONTENT to fill
       * the viewport when stuck, not the spacer.
       */
      var spacer = layer.querySelector('.selvane-dwell-spacer');
      var spacerHeight = spacer ? spacer.offsetHeight : 0;
      var contentHeight = layerHeight - spacerHeight;

      layer.style.position = 'sticky';

      /* Dynamic top: negative for tall layers, 0 for short ones */
      if (contentHeight > vh) {
        var topVal = -(contentHeight - vh);
        layer.style.top = topVal + 'px';
      } else {
        layer.style.top = '0px';
      }

      /* No z-index — DOM order handles stacking (later = on top) */

      /* Ensure child sections are normal flow */
      var childSections = layer.querySelectorAll('.shopify-section');
      for (var j = 0; j < childSections.length; j++) {
        childSections[j].style.position = 'relative';
        childSections[j].style.top = 'auto';
      }

      /* Ensure drawer-reveal children are relative */
      var drawers = layer.querySelectorAll('.selvane-drawer-reveal');
      for (var d = 0; d < drawers.length; d++) {
        drawers[d].style.position = 'relative';
        drawers[d].style.top = 'auto';
        drawers[d].style.zIndex = '1';
      }
    }
  }


  /* ═══════════════════════════════════════════════════════════════
   *  FIX PRODUCT GRID — Ensure images have aspect-ratio fallback
   *
   *  Problem: Lazy-loaded images have no natural dimensions,
   *  causing grid rows to collapse to ~33px.
   *  Fix: Set aspect-ratio: 3/4 on all product images (Brand Bible).
   * ═══════════════════════════════════════════════════════════════ */
  function fixProductGridImages() {
    /* Product shelf hero images */
    var heroImgs = $$('.selvane-product-shelf__hero-img');
    for (var h = 0; h < heroImgs.length; h++) {
      heroImgs[h].style.aspectRatio = '3/4';
      heroImgs[h].style.width = '100%';
      heroImgs[h].style.height = '100%';
      heroImgs[h].style.objectFit = 'cover';
    }

    /* Product shelf grid images */
    var gridImgs = $$('.selvane-product-shelf__grid-img');
    for (var g = 0; g < gridImgs.length; g++) {
      gridImgs[g].style.aspectRatio = '3/4';
      gridImgs[g].style.width = '100%';
      gridImgs[g].style.objectFit = 'cover';
      gridImgs[g].style.height = 'auto';
      gridImgs[g].style.padding = '0';
      gridImgs[g].style.boxSizing = 'border-box';
    }

    /* Product shelf grid items — ensure they size correctly */
    var gridItems = $$('.selvane-product-shelf__grid-item');
    for (var i = 0; i < gridItems.length; i++) {
      gridItems[i].style.overflow = 'hidden';
    }

    /* Four-up grid images */
    var fourUpImgs = $$('.selvane-four-up__img, .selvane-four-up-grid__img');
    for (var f = 0; f < fourUpImgs.length; f++) {
      fourUpImgs[f].style.aspectRatio = '3/4';
      fourUpImgs[f].style.width = '100%';
      fourUpImgs[f].style.objectFit = 'cover';
    }

    /* Four-up grid items */
    var fourUpItems = $$('.selvane-four-up__item, .selvane-four-up-grid__item');
    for (var fi = 0; fi < fourUpItems.length; fi++) {
      fourUpItems[fi].style.overflow = 'hidden';
    }

    /* Force product shelf gap to 0 */
    var shelves = $$('.selvane-product-shelf');
    for (var s = 0; s < shelves.length; s++) {
      shelves[s].style.gap = '0px';
    }

    /* Force four-up gap to 0 */
    var fourUps = $$('.selvane-four-up, .selvane-four-up-grid');
    for (var fu = 0; fu < fourUps.length; fu++) {
      fourUps[fu].style.gap = '0px';
    }
  }


  /* ═══════════════════════════════════════════════════════════════
   *  SCROLL HANDLER
   * ═══════════════════════════════════════════════════════════════ */
  function onScroll() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(handleScroll);
  }

  function handleScroll() {
    var y = window.scrollY || window.pageYOffset;

    /* ── Logo: fade out after scrolling past hero ── */
    if (malachiteLogo) {
      var logoScrollThreshold = 200;
      if (y > logoScrollThreshold) {
        malachiteLogo.classList.add('is-scrolled');
      } else {
        malachiteLogo.classList.remove('is-scrolled');
      }
    }

    /* ── Zone tracking (25% trigger line) ── */
    var trigger = window.innerHeight * 0.25;
    if (zoneMapDirty) {
      cachedZones = buildZoneMap();
      zoneMapDirty = false;
    }
    for (var z = 0; z < cachedZones.length; z++) {
      var r = cachedZones[z].el.getBoundingClientRect();
      cachedZones[z].top = r.top;
      cachedZones[z].bottom = r.bottom;
    }
    var zones = cachedZones;
    var currentTitle = '';

    for (var i = 0; i < zones.length; i++) {
      if (zones[i].top <= trigger && zones[i].title) {
        currentTitle = zones[i].title;
      }
    }

    if (currentTitle !== activeZoneTitle) {
      activeZoneTitle = currentTitle;
      updateStickyBarTitle(currentTitle);
    }

    /* ── StickyBar activation: Mutual Exclusion with Header ── */
    var activateThreshold = headerHeight + 200;
    var deactivateThreshold = headerHeight;

    var shouldActivate;
    if (stickyBarActive) {
      shouldActivate = y > deactivateThreshold && activeZoneTitle !== '';
    } else {
      shouldActivate = y > activateThreshold && activeZoneTitle !== '';
    }

    if (shouldActivate !== stickyBarActive) {
      stickyBarActive = shouldActivate;

      if (stickyBarEl) {
        stickyBarEl.classList.toggle('is-active', stickyBarActive);
      }

      if (sectionHeader) {
        sectionHeader.classList.toggle('selvane-header-hidden', stickyBarActive);
      }
    }

    /* ── Re-apply sticky styles (guard against external interference) ── */
    applyStackStyles();
  }


  /* ═══════════════════════════════════════════════════════════════
   *  UPDATE STICKY BAR TITLE
   * ═══════════════════════════════════════════════════════════════ */
  function updateStickyBarTitle(title) {
    if (!stickyBarEl) return;

    var titleEl = stickyBarEl.querySelector('.selvane-sticky-bar__title');
    if (titleEl) titleEl.textContent = title || '';

    var navEl = stickyBarEl.querySelector('.selvane-sticky-bar__nav');
    if (navEl) navEl.innerHTML = '';
  }


  /* ═══════════════════════════════════════════════════════════════
   *  FADE REVEAL — IO-based entrance for text blocks
   * ═══════════════════════════════════════════════════════════════ */
  function initFadeReveals() {
    var items = $$('.selvane-fade-reveal');
    for (var i = 0; i < items.length; i++) {
      (function (el) {
        if (el.dataset.observing === 'true') return;
        el.dataset.observing = 'true';

        try {
          var obs = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting) {
              el.classList.add('is-visible');
            } else {
              var rect = el.getBoundingClientRect();
              if (rect.bottom < 0 || rect.top > window.innerHeight) {
                el.classList.remove('is-visible');
              }
            }
          }, { threshold: 0.1, rootMargin: '0px 0px -10px 0px' });
          obs.observe(el);
        } catch (err) {
          el.classList.add('is-visible');
        }
      })(items[i]);
    }
  }


  /* ═══════════════════════════════════════════════════════════════
   *  LATE-ARRIVING SECTIONS — Re-scan for new elements
   * ═══════════════════════════════════════════════════════════════ */
  function rescanElements() {
    buildDrawerStack();
    fixProductGridImages();
    initFadeReveals();
    initHeroVideos();
  }


  /* ═══════════════════════════════════════════════════════════════
   *  HERO VIDEO MANAGEMENT (v21.7)
   *  - Auto-play videos when they enter viewport
   *  - Pause when they leave (save bandwidth)
   *  - Ensure muted + playsinline for mobile autoplay
   * ═══════════════════════════════════════════════════════════════ */
  function initHeroVideos() {
    var videos = $$('.selvane-corr-media__video, .selvane-editorial-hero__video, .selvane-hero-media__video');
    for (var v = 0; v < videos.length; v++) {
      (function (video) {
        if (video.dataset.svVideoInit === 'true') return;
        video.dataset.svVideoInit = 'true';

        /* Ensure autoplay attributes */
        video.muted = true;
        video.playsInline = true;
        video.loop = true;

        /* IO-based play/pause */
        try {
          var obs = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting) {
              video.play().catch(function () {});
            } else {
              video.pause();
            }
          }, { threshold: 0.15 });
          obs.observe(video);
        } catch (err) {
          video.play().catch(function () {});
        }
      })(videos[v]);
    }
  }


  /* ═══════════════════════════════════════════════════════════════
   *  INIT
   * ═══════════════════════════════════════════════════════════════ */
  function init() {
    measureHeader();

    malachiteLogo = $('#selvane-malachite-logo') || $('.selvane-malachite-logo');

    positionMalachiteLogo();

    stickyBarEl = renderStickyBar();

    /* Fix product grid images first (before building stack, so heights are correct) */
    fixProductGridImages();

    /* Build the stacking cards wrapper with grouped layers */
    buildDrawerStack();

    /* Scroll listener */
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      measureHeader();
      positionMalachiteLogo();
      zoneMapDirty = true;
      applyStackStyles();
      onScroll();
    }, { passive: true });

    /* Initial calculation */
    handleScroll();

    /* Fade reveals */
    initFadeReveals();

    /* v21.7: Initialize hero video management */
    initHeroVideos();

    /* Re-scan for Shopify's lazy section loading */
    var rescanDelays = [500, 1000, 2000, 3500, 5000, 8000];
    for (var d = 0; d < rescanDelays.length; d++) {
      (function (delay) {
        setTimeout(function () {
          zoneMapDirty = true;
          rescanElements();
        }, delay);
      })(rescanDelays[d]);
    }

    /* Re-measure header after Shopify finishes loading */
    setTimeout(function () {
      measureHeader();
      positionMalachiteLogo();
    }, 300);
    setTimeout(function () {
      measureHeader();
      positionMalachiteLogo();
    }, 800);
    setTimeout(function () {
      measureHeader();
      positionMalachiteLogo();
    }, 1500);

    /* MutationObserver for dynamically added sections */
    try {
      var mainContent = $('main') || document.body;
      var mutObs = new MutationObserver(function (mutations) {
        var shouldRescan = false;
        for (var m = 0; m < mutations.length; m++) {
          if (mutations[m].addedNodes.length > 0) {
            shouldRescan = true;
            break;
          }
        }
        if (shouldRescan) {
          zoneMapDirty = true;
          setTimeout(rescanElements, 100);
          setTimeout(rescanElements, 500);
        }
      });
      mutObs.observe(mainContent, { childList: true, subtree: true });
    } catch (err) {
      /* MutationObserver not supported */
    }
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
